import { useContext, useState } from "react";

import {
  Box,
  Stack,
  Typography,
  IconButton,
  Modal,
  Grid,
  useMediaQuery,
  Theme,
  Paper,
} from "@mui/material";
import {
  AddCircleOutlined,
  ArrowBackOutlined,
  RemoveOutlined,
  HighlightOffTwoTone as CloseIcon,
} from "@mui/icons-material";

import PerfectScrollbar from "react-perfect-scrollbar";

import MainCard from "components/mainCard";

import InnerOptionSet from "../innerOptionSet";
import { OptionSetContext } from "features/components/optionSetContext";
import { replaceItemAtIndexInArray } from "features/components/helperFunctions";
import { modalPaperStyles, optionItemBoxStyles } from "../index.styles";

interface OptionSetItemsProps {
  parentIndex: number;
  itemIndex: number;
  item: any;
}

const OptionSetItems = (props: OptionSetItemsProps) => {
  const {
    options,
    setOptions,
    indexOptionSet,
    setIndexOptionSet,
    optionSetEdit,
    setOptionSetEdit,
    selectedOrderContext,
  } = useContext(OptionSetContext);

  const [item] = useState(props.item);
  const [itemIndex] = useState(props.itemIndex);
  const [parentIndex] = useState(props.parentIndex);
  const [option, setOption] = useState(options[parentIndex]);
  const [isInnerOption, setInnerOptionSet] = useState<boolean>(false);
  const currency = selectedOrderContext.currency;

  const isScreenSmall = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("sm")
  );

  const hasInnerOptions = () => {
    if (item.items.length > 0) {
      return true;
    }
    return false;
  };

  const onIncrementClicked = () => {
    setOptionSetEdit(true);

    let quantity = options[parentIndex].items[itemIndex].quantity;
    let max = options[parentIndex].max;
    let totalSelected = options[parentIndex].totalSelected;
    if (totalSelected < max) {
      if (hasInnerOptions()) {
        if (quantity < 1) {
          // it means inner option set have been selected
          toggleInnerOptionModal();
        } else {
          quantity += 1;
          totalSelected += 1;
          updateTotalSelectedQuantityAndSelection(
            totalSelected,
            quantity,
            true
          );
          // auto slide to next option set
          if (parseInt(totalSelected) === parseInt(max)) {
            let currentSlide = indexOptionSet;
            let totalSlides = options.length;
            if (currentSlide < totalSlides - 1) {
              setIndexOptionSet((currentSlide + 1) % totalSlides);
            }
          }
        }
      } else {
        quantity += 1;
        totalSelected += 1;
        updateTotalSelectedQuantityAndSelection(totalSelected, quantity, true);
        // auto slide to next option set
        if (parseInt(totalSelected) === parseInt(max)) {
          let currentSlide = indexOptionSet;
          let totalSlides = options.length;
          if (currentSlide < totalSlides - 1) {
            setIndexOptionSet((currentSlide + 1) % totalSlides);
          }
        }
      }
    }
  };

  const updateTotalSelectedQuantityAndSelection = (
    totalSelected: number,
    quantity: number,
    isSelected: boolean
  ) => {
    let localOptions = options;
    let localOption = options[parentIndex];
    let localItems = options[parentIndex].items;
    let localItem = options[parentIndex].items[itemIndex];

    localItem.quantity = quantity;
    localItem.isSelected = isSelected;
    localOption.totalSelected = totalSelected;

    localItems = replaceItemAtIndexInArray(itemIndex, localItem, localItems);
    localOption.items = localItems;
    localOptions = replaceItemAtIndexInArray(parentIndex, localOption, options);
    setOptions(localOptions);
  };

  const onDecrementClicked = () => {
    setOptionSetEdit(true);
    let quantity = options[parentIndex].items[itemIndex].quantity;
    let totalSelected = options[parentIndex].totalSelected;

    let isSelected = true;
    if (quantity > 0) {
      quantity -= 1;
      totalSelected -= 1;
      if (quantity === 0) {
        isSelected = false;
      }
      updateTotalSelectedQuantityAndSelection(
        totalSelected,
        quantity,
        isSelected
      );
    }
  };

  const incrementAfterInnerOptionsSelected = () => {
    var quantity = options[parentIndex].items[itemIndex].quantity;
    var max = options[parentIndex].max;
    var totalSelected = options[parentIndex].totalSelected;
    if (totalSelected < max) {
      quantity += 1;
      totalSelected += 1;
      updateTotalSelectedQuantityAndSelection(totalSelected, quantity, true);
    }
  };

  const toggleInnerOptionModal = () => {
    setInnerOptionSet((toggle) => !toggle);
  };

  return (
    <>
      <Box sx={optionItemBoxStyles}>
        <Grid container sx={{ display: "flex", alignItems: "center" }}>
          {/* 1-  Name and Price */}
          <Grid item xs={7} md={9}>
            <Typography variant={"body1"}> {item.name} </Typography>
          </Grid>

          {/* controls */}
          <Grid
            item
            xs={5}
            md={3}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "end",
            }}
          >
            <Stack
              direction="row"
              spacing={0.25}
              sx={{ alignItems: "end", pr: { xs: "8px", md: "16px" } }}
            >
              <Typography
                sx={{ fontStyle: "normal", fontSize: "10px", color: "#757575" }}
              >
                {currency}
              </Typography>

              <Typography
                variant={isScreenSmall ? "subtitle1" : "h4"}
                sx={{ color: "#212121" }}
              >
                {item.price}
              </Typography>
            </Stack>

            <IconButton
              color="inherit"
              sx={{ p: "unset" }}
              onClick={onDecrementClicked}
            >
              <RemoveOutlined
                sx={{
                  boxSizing: "border-box",
                  width: { xs: "18px", md: "32px" },
                  height: { xs: "18px", md: "32px" },
                  background: "#F5F5F5",
                  color: "#212121",
                  borderRadius: "2em",
                }}
              />
            </IconButton>

            <Typography
              variant={"body1"}
              sx={{ p: { xs: "0 8px", md: "0 16px" } }}
            >
              {item.quantity}
            </Typography>

            <IconButton
              color="inherit"
              sx={{ background: "#FFFFFF", p: "unset" }}
              onClick={onIncrementClicked}
            >
              <AddCircleOutlined
                sx={{
                  width: { xs: "18px", md: "32px" },
                  height: { xs: "18px", md: "32px" },
                  color: "#DB154D",
                }}
              />
            </IconButton>
          </Grid>
        </Grid>
      </Box>

      <Modal //Inner option set Modal
        open={isInnerOption}
        onClose={toggleInnerOptionModal}
      >
        <Paper sx={{ ...modalPaperStyles, zIndex: 2 }}>
          <PerfectScrollbar
            style={{
              height: "100%",
              maxHeight: "90vh",
              overflowX: "hidden",
              borderRadius: "8px",
            }}
          >
            <MainCard
              dividerSX={{ m: "0px 0px 33px 0px !important" }}
              headerSX={{ p: "unset !important", mb: "16px" }}
              contentSX={{
                "& .MuiCardContent-root": {
                  p: "unset !important",
                },
                m: "unset",
                p: "unset !important",
              }}
              sx={{
                m: "unset",
                p: { xs: "18px", md: "32px", lg: "40px" },
                border: "none",
              }}
              title={
                <Grid container>
                  <Grid
                    item
                    xs={12}
                    sx={{ display: "flex", alignItems: "center" }}
                  >
                    <Grid
                      item
                      xs={10}
                      sx={{ display: "flex", alignItems: "center" }}
                    >
                      <IconButton
                        onClick={toggleInnerOptionModal}
                        sx={{ p: "unset", mr: "11px" }}
                      >
                        <ArrowBackOutlined />
                      </IconButton>

                      <Typography variant={"h3"}>Inner Option Sets</Typography>
                    </Grid>

                    <Grid
                      item
                      xs={2}
                      sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        alignItems: "center",
                      }}
                    >
                      <IconButton
                        onClick={toggleInnerOptionModal}
                        sx={{ p: "unset" }}
                      >
                        <CloseIcon htmlColor="#D84315" fontSize="large" />
                      </IconButton>
                    </Grid>
                  </Grid>
                </Grid>
              }
            >
              <InnerOptionSet
                itemIndex={itemIndex}
                parentIndex={parentIndex}
                toggleInnerOptionModal={toggleInnerOptionModal}
                incrementAfterInnerOptionsSelected={
                  incrementAfterInnerOptionsSelected
                }
              />
            </MainCard>
          </PerfectScrollbar>
        </Paper>
      </Modal>
    </>
  );
};

export default OptionSetItems;
